import React, { ReactNode, useEffect, useRef, useState } from "react";
import { ArrowRightIcon } from "../icons";
import SVGIcon from "./svg_icon";

interface AccordionProps {
  title: string;
  subtitle: string;
  children?: ReactNode;
  extraComponent?: ReactNode;
  onToggle?: (isOpen: boolean) => void;
  initialIsOpen?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  subtitle,
  children,
  extraComponent,
  onToggle,
  initialIsOpen = false,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(initialIsOpen);
  const [maxHeight, setMaxHeight] = useState("0px");
  const contentRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    const newIsOpen = !internalIsOpen;
    setInternalIsOpen(newIsOpen);

    if (contentRef.current) {
      setMaxHeight(newIsOpen ? `${contentRef.current.scrollHeight}px` : "0px");
    }

    if (onToggle) {
      onToggle(newIsOpen);
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(
        internalIsOpen ? `${contentRef.current.scrollHeight}px` : "0px"
      );
    }
  }, [internalIsOpen]);

  const hasChildren = children !== null && children !== undefined;

  return (
    <div className="flex w-full">
      <div className="flex-grow accordion px-6 py-6 bg-gray-100 ml-4 mr-4 my-1 rounded-lg">
        <button
          className={`flex justify-between items-center w-full text-lg font-black focus:outline-none transition-colors ${
            !hasChildren || internalIsOpen ? "text-sky-700" : "text-gray-400"
          }`}
          onClick={hasChildren ? handleToggle : undefined}
        >
          {title}
          {hasChildren && (
            <span
              className={`transition-transform duration-300 ease-out ${
                internalIsOpen ? "rotate-90" : ""
              }`}
            >
              <SVGIcon scale="scale-100" IconComponent={ArrowRightIcon} />
            </span>
          )}
        </button>
        
        <div className="flex pt-2 items-center">
          {extraComponent && (
            <div className="extra-component">{extraComponent}</div>
          )}
          <div className="text-gray-600 text-sm">{subtitle}</div>
        </div>
        {hasChildren && (
          <div
            className={`accordion-content ${internalIsOpen ? "open" : ""}`}
            ref={contentRef}
            style={{ maxHeight: `${maxHeight}` }}
          >
            <div className="pt-4">{children}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Accordion;
